import { inject, Injectable } from "@angular/core";
import { ValueOf } from "@cureskin/api-client/src/common";
import { ApiClientConstant } from "@cureskin/api-client/src/constant/api-client.constant";
import { User } from "@cureskin/api-client/src/server-api";
import { ConnectionService } from "@services/connection-service";

export type ActivePregnancyTag =
  | typeof ApiClientConstant.User.MarketingTags.PREGNANT
  | typeof ApiClientConstant.User.MarketingTags.NEW_MOM
  | typeof ApiClientConstant.User.MarketingTags.PLANNING_FOR_PREGNANCY;

const BeforeOrderPlacedStages = [
  ApiClientConstant.User.OrderState.NEW_USER,
  ApiClientConstant.User.OrderState.UNPAID,
];

const UnpaidUserOrderStates = [
    ...BeforeOrderPlacedStages,
  ApiClientConstant.User.OrderState.PROCESSING,
];

@Injectable({providedIn: 'root'})
export class UserService {
  private readonly connectionService = inject(ConnectionService);

  readonly devnagriScripts: ReadonlyArray<string> = [
    ApiClientConstant.LanguageString.HI,
    ApiClientConstant.LanguageString.MR,
  ];

  readonly dravidianScripts: ReadonlyArray<string> = [
    ApiClientConstant.LanguageString.KN,
    ApiClientConstant.LanguageString.TE,
  ];

  getUser(): User {
    return this.connectionService.getCurrentUser();
  }

  getUserLanguagePreference(): string {
    return this.connectionService.getCurrentUser()?.get('languagePreference') ?? ApiClientConstant.LanguageString.EN;
  }

  getUserGender(): string {
    return this.connectionService.getCurrentUser()?.get('Gender') ?? ApiClientConstant.User.Gender.FEMALE;
  }

  isPaidUser(user: User | Table.User): boolean {
    return !this.isUnpaidUser(user);
  }

  isUnpaidUser(user: User | Table.User): boolean {
    return UnpaidUserOrderStates.includes(user.get('orderState'));
  }

  hasNotPlacedOrder(user: User | Table.User): boolean {
    return BeforeOrderPlacedStages.includes(user.get('orderState'));
  }

  isNewOrUnpaidUser(user: User | Table.User): boolean {
    const orderState = user.get('orderState');
    return orderState === ApiClientConstant.User.OrderState.NEW_USER
      || orderState === ApiClientConstant.User.OrderState.UNPAID;
  }

  hasPregnancyRelatedTags(marketingTags: Array<ValueOf<typeof ApiClientConstant.User.MarketingTags>>): boolean {
    return marketingTags.includes(ApiClientConstant.User.MarketingTags.PREGNANT) ||
      marketingTags.includes(ApiClientConstant.User.MarketingTags.NEW_MOM) ||
      marketingTags.includes(ApiClientConstant.User.MarketingTags.PLANNING_FOR_PREGNANCY);
  }

  getActivePregnancyTag(marketingTags: Array<ValueOf<typeof ApiClientConstant.User.MarketingTags>>): ActivePregnancyTag | null {
    if (marketingTags.includes(ApiClientConstant.User.MarketingTags.NEW_MOM)) return ApiClientConstant.User.MarketingTags.NEW_MOM;
    if (marketingTags.includes(ApiClientConstant.User.MarketingTags.PREGNANT)) return ApiClientConstant.User.MarketingTags.PREGNANT;
    if (marketingTags.includes(ApiClientConstant.User.MarketingTags.PLANNING_FOR_PREGNANCY)) return ApiClientConstant.User.MarketingTags.PLANNING_FOR_PREGNANCY;
    return null;
  }
}
